import React, { useState } from "react";
import {
    Container,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Box,
    Stack,
} from "@mui/material";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import SubtitlesRoundedIcon from "@mui/icons-material/SubtitlesRounded";
import axios from "axios";
import { API_URL } from "../config";
import { SectionHeading } from "../utils";

const RECORD_NAME_MAX_LENGTH = 25;

export default function CreateSpecialRecord({ type }) {
    const isWebsite = type === "website";
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleCreate = async (e) => {
        e.preventDefault();

        const trimmedName = name.trim();
        if (!trimmedName) {
            setErrorMessage("Record name is required.");
            return;
        }

        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        const formData = new FormData();
        formData.append("name", trimmedName);
        formData.append("type", isWebsite ? "website" : "survey");

        try {
            const response = await axios.post(API_URL + "create_special_record.php", formData, {
                withCredentials: true,
            });

            if (response.data?.success) {
                setSuccessMessage(response.data.message || "Record created successfully.");
                setName("");
            } else {
                setErrorMessage(response.data?.message || "Failed to create record.");
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "Failed to create record. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md" className="form-shell upload-url-shell">
            <Box className="form-shell-badge">
                {isWebsite ? <LanguageRoundedIcon fontSize="small" /> : <SubtitlesRoundedIcon fontSize="small" />}
                <Typography variant="caption">{isWebsite ? "Website QR" : "Survey QR"}</Typography>
            </Box>

            <SectionHeading sx={{ marginTop: "-8px" }}>
                {isWebsite ? "Create a website QR record" : "Create a survey QR record"}
            </SectionHeading>

            <Typography variant="h6" gutterBottom className="form-subheading">
                {isWebsite
                    ? "Launch a new website QR and choose the visible linked QRs from the edit screen."
                    : "Launch a new survey QR and customize its questions and response flow."}
            </Typography>

            <form onSubmit={handleCreate} className="form-stack">
                <Stack spacing={1.5}>
                    <TextField
                        label="Record Name"
                        helperText={`${name.length}/${RECORD_NAME_MAX_LENGTH} characters`}
                        value={name}
                        onChange={(e) => setName(e.target.value.slice(0, RECORD_NAME_MAX_LENGTH))}
                        fullWidth
                        required
                        variant="outlined"
                        className="form-input"
                        inputProps={{ maxLength: RECORD_NAME_MAX_LENGTH }}
                    />
                </Stack>

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="form-submit-btn"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : `Create ${isWebsite ? "Website" : "Survey"}`}
                </Button>
            </form>

            {successMessage && (
                <Snackbar open={true} autoHideDuration={4000} onClose={() => setSuccessMessage("")}>
                    <Alert severity="success" onClose={() => setSuccessMessage("")}>{successMessage}</Alert>
                </Snackbar>
            )}

            {errorMessage && (
                <Snackbar open={true} autoHideDuration={4000} onClose={() => setErrorMessage("")}>
                    <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </Snackbar>
            )}
        </Container>
    );
}
